/* eslint-disable react/display-name */
import React from 'react';
import styled from 'styled-components';
import { slate1, white } from '@engage/colors';
import { spaces } from '@engage/styles';
import { label14 } from '@engage/typography';
import { track } from '@members/analytics';
import { TopNav, TopNavCopy } from '@members/copy';
import {
  CircledPlay,
  Collections as CollectionsIcon,
  Programs as ProgramsIcon,
} from '@members/icons';
import { COLLECTIONS_ROUTE } from '@members/pg-collections';
import { trackViewedClassGroupLibrary } from '@members/pg-homescreen/analytics';
import { LIBRARY_CATEGORY_SELECTION_ROUTE } from '@members/pg-library/urls';
import { PROGRAMS_ROUTE } from '../../../pg-programs/urls';
import { MainLink, makeLinkActivatable } from '../../sharedLinkStyles';
import { ANALYTICS_SOURCE } from '../analytics';

type WorkoutsDropdownProps = {
  isMenuOpen: boolean;
};

export const WorkoutsDropdown = React.forwardRef<HTMLUListElement, WorkoutsDropdownProps>(
  (props, ref) => {
    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (!props.isMenuOpen) return;

      const links = document.querySelectorAll('.dropdown-link');
      const current = document.activeElement;
      let nextIndex = Array.from(links).findIndex(link => link === current);

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          nextIndex = nextIndex + 1 >= links.length ? 0 : nextIndex + 1;
          (links[nextIndex] as HTMLElement).focus();
          break;

        case 'ArrowUp':
          event.preventDefault();
          nextIndex = nextIndex - 1 < 0 ? links.length - 1 : nextIndex - 1;
          (links[nextIndex] as HTMLElement).focus();
          break;

        case ' ':
          event.preventDefault();
          if (current instanceof HTMLElement) {
            current.click();
          }
          break;

        case 'Tab':
          // If we're on the last item and tabbing forward, or first item and tabbing backward
          if (
            (!event.shiftKey && nextIndex === links.length - 1) ||
            (event.shiftKey && nextIndex === 0)
          ) {
            // Find and click the Workouts button to close the menu
            const workoutsButton = document.querySelector(
              '[aria-controls="workouts-menu"]',
            ) as HTMLElement;
            workoutsButton?.click();
          }
          break;
      }
    };

    return (
      <WorkoutsContainer
        id="workouts-menu"
        role="menu"
        aria-hidden={!props.isMenuOpen}
        isMenuOpen={props.isMenuOpen}
        ref={ref}
        onKeyDown={handleKeyDown}
      >
        <li role="none">
          <ClassesLink />
        </li>
        <li role="none">
          <ProgramsLink />
        </li>
        <li role="none">
          <CollectionsLink />
        </li>
      </WorkoutsContainer>
    );
  },
);

const ClassesLink = () => (
  <WorkoutsDropdownLink
    className="dropdown-link"
    to={{
      pathname: LIBRARY_CATEGORY_SELECTION_ROUTE,
      state: { source: ANALYTICS_SOURCE },
    }}
    data-test-id="classesLink"
    role="menuitem"
  >
    <CircledPlay />
    <TopNavCopy id={TopNav.Classes} />
  </WorkoutsDropdownLink>
);

const CollectionsLink = () => {
  const trackAnalyticsLink = () => track(trackViewedClassGroupLibrary('Top Nav'));

  return (
    <WorkoutsDropdownLink
      className="dropdown-link"
      to={{
        pathname: COLLECTIONS_ROUTE,
        state: { source: ANALYTICS_SOURCE },
      }}
      data-test-id="collectionsLink"
      onClick={trackAnalyticsLink}
      role="menuitem"
    >
      <CollectionsIcon />
      <TopNavCopy id={TopNav.Collections} />
    </WorkoutsDropdownLink>
  );
};

const ProgramsLink = () => (
  <WorkoutsDropdownLink
    className="dropdown-link"
    to={{
      pathname: PROGRAMS_ROUTE,
      state: { source: ANALYTICS_SOURCE },
    }}
    data-test-id="programsLink"
    role="menuitem"
  >
    <ProgramsIcon />
    <TopNavCopy id={TopNav.Programs} />
  </WorkoutsDropdownLink>
);

const StyledLink = styled(MainLink)`
  ${label14}
  margin-bottom: 0;
  display: flex;
  align-items: center;
  padding: ${spaces.small}px;
  gap: ${spaces.small}px;
  color: ${slate1};
  fill: ${slate1};
  background: ${white};

  &:hover {
    color: ${slate1};
    fill: ${slate1};
  }

  &:focus {
    outline: 2px solid #00a7e1;
    outline-offset: -2px;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const WorkoutsDropdownLink = makeLinkActivatable(StyledLink);

const WorkoutsContainer = styled.ul<{ isMenuOpen: boolean }>`
  border-radius: 4px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
  position: absolute;
  top: 48px;
  width: 166px;
  background: ${white};
  ${p => !p.isMenuOpen && `display: none;`}
`;
